import { useMemo } from "react";
import {
  proposalUnsavedChangeStatus,
  proposalUnsubmittedChangeStatus,
} from "work/proposals/_views/_shared/proposal-change-share-plan";
import type {
  ProposalCollaborationEnvelope,
  ProposalContentOperation,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalPendingChangeCountsByCategory,
  proposalResolvedChangeCountsByCategory,
  proposalUnsubmittedChangeCountsByCategory,
} from "work/proposals/negotiation/proposal-pending-change-counts";
import { type ProposalFieldCategoryType } from "work/proposals/proposal-fields/proposal-field";

/**
 * What a proposal tab advertises about the actor's own work that has not reached its audience yet: the
 * edits they have not saved, and — on an active proposal — the saves they have not submitted, and the
 * counterparty's incoming changes their side has already resolved (#7086).
 *
 * Both pages carry the same six tabs, and both used to derive this inline — which is how they came to
 * pass a hard-coded zero for each of them after the legacy editor was deleted, leaving
 * `pending-change-badge` unable to render at all. One hook so the two pages cannot drift again.
 *
 * Each count carries the phrase that says what it is waiting for, because that depends on the page and
 * not on the tab. A draft with no reviewers has nobody to share a save with, so its unsaved changes are
 * "not yet saved", not "not yet shared" (#5812).
 *
 * Distinct from the unviewed badge beside it: that counts what SOMEBODY ELSE did and the viewer has not
 * looked at. This counts what the viewer themselves has staged and not yet sent.
 */
export type ProposalTabPendingCounts = {
  /** The badge props for one tab, spread onto `ProposalIssuesBadge`. */
  forCategory: (category: ProposalFieldCategoryType) => ProposalTabPendingBadgeProps;
  unsavedChangeCount: number;
  unsavedChangeStatus: string;
  unsubmittedChangeCount: number;
  unsubmittedChangeStatus: string;
  resolvedChangeCount: number;
};

export type ProposalTabPendingBadgeProps = {
  pendingChangeCount: number;
  pendingChangeStatus: string;
  unsubmittedChangeCount: number;
  unsubmittedChangeStatus: string;
  resolvedChangeCount: number;
};

export default function useProposalTabPendingCounts(args: {
  /** Exactly what the next Save will persist; empty outside the canonical editor. */
  pendingOperations: readonly ProposalContentOperation[] | undefined;
  /** Who the next Save reaches — the same inputs as `proposalSaveAudienceLabel`. */
  reviewerCount: number;
  isReviewing: boolean;
  /**
   * The envelope whose saved-but-unpublished lines count as "not yet submitted", and whose resolution
   * states count as resolved. Only an active proposal passes one: a draft has never been submitted, so
   * every term on it would count, and nothing on it has come from a counterparty to resolve.
   */
  submissionEnvelope?: ProposalCollaborationEnvelope;
}): ProposalTabPendingCounts {
  const { pendingOperations, reviewerCount, isReviewing, submissionEnvelope } = args;
  const changeCounts = useMemo(() => proposalPendingChangeCountsByCategory(pendingOperations), [pendingOperations]);
  const unsubmittedCounts = useMemo(
    () => proposalUnsubmittedChangeCountsByCategory(submissionEnvelope),
    [submissionEnvelope],
  );
  const resolvedCounts = useMemo(() => proposalResolvedChangeCountsByCategory(submissionEnvelope), [submissionEnvelope]);
  const unsavedChangeStatus = proposalUnsavedChangeStatus({ reviewerCount, isReviewing });
  const unsubmittedChangeStatus = proposalUnsubmittedChangeStatus(submissionEnvelope?.viewingParty);

  return {
    forCategory: (category) => ({
      pendingChangeCount: changeCounts[category] ?? 0,
      pendingChangeStatus: unsavedChangeStatus,
      unsubmittedChangeCount: unsubmittedCounts[category] ?? 0,
      unsubmittedChangeStatus,
      resolvedChangeCount: resolvedCounts[category] ?? 0,
    }),
    unsavedChangeCount: sumCounts(changeCounts),
    unsavedChangeStatus,
    unsubmittedChangeCount: sumCounts(unsubmittedCounts),
    unsubmittedChangeStatus,
    resolvedChangeCount: sumCounts(resolvedCounts),
  };
}

function sumCounts(counts: Partial<Record<ProposalFieldCategoryType, number>>): number {
  return Object.values(counts).reduce((total, count) => total + (count ?? 0), 0);
}
